/**
 * DynamicForm — 根据后端返回的 form_schema 动态渲染 Ant Design 表单
 *
 * 支持的 form_schema 类型（7种）：
 *   string     → Input
 *   textarea   → TextArea
 *   select     → Select
 *   number     → InputNumber
 *   file       → Upload（单文件）
 *   file-list  → Upload（多文件）
 *   password   → Input.Password
 *
 * Props:
 *   schema    — Array<{key, label, type, required, default, options?, min?, max?, max_length?, placeholder?}>
 *   form      — Ant Design Form 实例（Form.useForm()）
 *   loading   — 布尔值，控制加载态
 *   onValuesChange — (changedValues, allValues) => void
 */
import React, { useMemo, useCallback } from 'react';
import {
  Form,
  Input,
  Select,
  InputNumber,
  Upload,
  Button,
  Spin,
  Empty,
  Typography,
  message,
} from 'antd';
import { UploadOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;
const { Text } = Typography;
const { Dragger } = Upload;

// ── 类型 → Ant Design 组件映射 ─────────────────────────────────
const TYPE_COMPONENT_MAP = {
  string:    { component: Input,    extraProps: {} },
  textarea:  { component: TextArea, extraProps: { rows: 4 } },
  select:    { component: Select,   extraProps: {} },
  number:    { component: InputNumber, extraProps: { style: { width: '100%' } } },
  file:      { component: 'UploadSingle', extraProps: {} },
  'file-list': { component: 'UploadMultiple', extraProps: {} },
  password:  { component: Input.Password, extraProps: {} },
};

/**
 * 单文件上传组件（内部封装 Upload）
 * 返回值：uploaded URL（string）或 null
 */
const SingleFileUpload = ({ value, onChange, accept, maxSizeMB }) => {
  const { t } = useTranslation();

  const handleBeforeUpload = useCallback((file) => {
    const maxSize = (maxSizeMB || 10) * 1024 * 1024;
    if (file.size > maxSize) {
      message.error(t('common.fileTooLarge') || '文件大小超过限制');
      return Upload.LIST_IGNORE;
    }
    // 上传文件到后端
    const formData = new FormData();
    formData.append('file', file);
    // 使用 fetch 上传
    fetch('/api/upload-chat-image/', {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          const backendUrl =
            window.BACKEND_URL ||
            process.env.REACT_APP_BACKEND_URL ||
            `${window.location.protocol}//${window.location.host}`;
          const url = data.url.startsWith('http') ? data.url : `${backendUrl}${data.url}`;
          onChange(url);
        } else {
          message.error(data.error || t('common.uploadFailed') || '上传失败');
        }
      })
      .catch((err) => {
        message.error(err.message || t('common.uploadFailed') || '上传失败');
      });
    return false; // 阻止默认上传
  }, [onChange, maxSizeMB, t]);

  const handleRemove = useCallback(() => {
    onChange(null);
  }, [onChange]);

  const fileList = value
    ? [
        {
          uid: '-1',
          name: value.split('/').pop() || 'file',
          status: 'done',
          url: value,
        },
      ]
    : [];

  return (
    <Upload
      accept={accept || '*'}
      fileList={fileList}
      beforeUpload={handleBeforeUpload}
      onRemove={handleRemove}
      maxCount={1}
    >
      <Button icon={<UploadOutlined />}>{t('common.clickToUpload') || '点击上传'}</Button>
    </Upload>
  );
};

/**
 * 多文件上传组件
 * 返回值：uploaded URL 数组
 */
const MultipleFileUpload = ({ value = [], onChange, accept, maxSizeMB, maxCount }) => {
  const { t } = useTranslation();

  // value 存储为 URL 数组
  const fileList = (value || []).map((url, index) => ({
    uid: `file_${index}_${Date.now()}`,
    name: url.split('/').pop() || `file_${index}`,
    status: 'done',
    url,
  }));

  const handleBeforeUpload = useCallback(
    (file) => {
      const maxSize = (maxSizeMB || 10) * 1024 * 1024;
      if (file.size > maxSize) {
        message.error(t('common.fileTooLarge') || '文件大小超过限制');
        return Upload.LIST_IGNORE;
      }
      const formData = new FormData();
      formData.append('file', file);
      fetch('/api/upload-chat-image/', {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            const backendUrl =
              window.BACKEND_URL ||
              process.env.REACT_APP_BACKEND_URL ||
              `${window.location.protocol}//${window.location.host}`;
            const url = data.url.startsWith('http') ? data.url : `${backendUrl}${data.url}`;
            const newValue = [...(value || []), url];
            onChange(newValue);
          } else {
            message.error(data.error || t('common.uploadFailed') || '上传失败');
          }
        })
        .catch((err) => {
          message.error(err.message || t('common.uploadFailed') || '上传失败');
        });
      return false;
    },
    [value, onChange, maxSizeMB, t],
  );

  const handleRemove = useCallback(
    (removedFile) => {
      const newValue = (value || []).filter((url) => url !== removedFile.url);
      onChange(newValue);
    },
    [value, onChange],
  );

  return (
    <Upload
      accept={accept || '*'}
      fileList={fileList}
      beforeUpload={handleBeforeUpload}
      onRemove={handleRemove}
      maxCount={maxCount || 10}
    >
      <Dragger style={{ padding: '12px 0' }}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{t('common.clickOrDragToUpload') || '点击或拖拽文件到此区域上传'}</p>
      </Dragger>
    </Upload>
  );
};

// ── 字段渲染器 ──────────────────────────────────────────────────
const renderFormItem = (field) => {
  const { key, label, type, required, default: defaultValue, options, min, max, max_length, placeholder } = field;
  const mapping = TYPE_COMPONENT_MAP[type] || TYPE_COMPONENT_MAP.string;

  // 构建 Form.Item 规则
  const rules = [];
  if (required) {
    rules.push({ required: true, message: `请填写${label}` });
  }
  if (type === 'string' && max_length) {
    rules.push({ max: max_length, message: `最多 ${max_length} 个字符` });
  }
  if (type === 'textarea' && max_length) {
    rules.push({ max: max_length, message: `最多 ${max_length} 个字符` });
  }
  if (type === 'number') {
    rules.push({ type: 'number', message: '请输入数字' });
    if (min !== undefined) rules.push({ min, message: `最小值为 ${min}` });
    if (max !== undefined) rules.push({ max, message: `最大值为 ${max}` });
  }

  // 构建组件 Props
  const componentProps = {
    placeholder: placeholder || `请输入${label}`,
    ...mapping.extraProps,
  };

  let Component = mapping.component;

  // select 类型特殊处理
  if (type === 'select') {
    componentProps.options = (options || []).map((opt) =>
      typeof opt === 'object' && opt.value ? opt : { value: opt, label: opt },
    );
  }

  // 文件上传特殊处理
  if (type === 'file') {
    Component = SingleFileUpload;
    componentProps.accept = field.accept;
    componentProps.maxSizeMB = field.max_size_mb;
    // valuePropName 为 value（自定义组件）
    return { Component, componentProps, rules, valuePropName: 'value' };
  }

  if (type === 'file-list') {
    Component = MultipleFileUpload;
    componentProps.accept = field.accept;
    componentProps.maxSizeMB = field.max_size_mb;
    componentProps.maxCount = field.max_count;
    return { Component, componentProps, rules, valuePropName: 'value' };
  }

  // number 类型 — 设置 min/max
  if (type === 'number') {
    if (min !== undefined) componentProps.min = min;
    if (max !== undefined) componentProps.max = max;
  }

  return { Component, componentProps, rules, valuePropName: 'value' };
};

// ── 主组件 ──────────────────────────────────────────────────────
const DynamicForm = ({ schema = [], form, loading = false, onValuesChange, layout = 'vertical' }) => {
  const { t } = useTranslation();

  // 计算初始值
  const initialValues = useMemo(() => {
    const values = {};
    (schema || []).forEach((field) => {
      if (field.default !== undefined && field.default !== null && field.default !== '') {
        values[field.key] = field.default;
      }
    });
    return values;
  }, [schema]);

  // 空状态
  const visibleSchema = useMemo(
    () => (schema || []).filter((field) => !field.hidden),
    [schema],
  );

  if (!visibleSchema || visibleSchema.length === 0) {
    return (
      <Spin spinning={loading}>
        <Empty
          description={t('common.noFormFields') || '该智能体无需额外参数'}
          style={{ margin: '24px 0' }}
        />
      </Spin>
    );
  }

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        layout={layout}
        initialValues={initialValues}
        onValuesChange={onValuesChange}
        style={{ width: '100%' }}
      >
        {visibleSchema.map((field) => {
          const { key, label, type, required } = field;
          const { Component, componentProps, rules, valuePropName } = renderFormItem(field);

          // Select 特殊处理
          if (type === 'select') {
            return (
              <Form.Item
                key={key}
                name={key}
                label={label}
                rules={rules}
                required={required}
                valuePropName={valuePropName || 'value'}
              >
                <Select
                  {...componentProps}
                  allowClear={!required}
                />
              </Form.Item>
            );
          }

          // Upload 类型特殊处理（自定义 valuePropName）
          if (type === 'file' || type === 'file-list') {
            return (
              <Form.Item
                key={key}
                name={key}
                label={label}
                rules={rules}
                required={required}
                valuePropName={valuePropName || 'value'}
                getValueFromEvent={(val) => val} // 自定义组件直接传递值
              >
                <Component {...componentProps} />
              </Form.Item>
            );
          }

          return (
            <Form.Item
              key={key}
              name={key}
              label={label}
              rules={rules}
              required={required}
              valuePropName={valuePropName || 'value'}
            >
              <Component {...componentProps} />
            </Form.Item>
          );
        })}
      </Form>
    </Spin>
  );
};

export { SingleFileUpload, MultipleFileUpload };
export default React.memo(DynamicForm);
